import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@material-ui/core";
import { Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import { Table, TableBody, TableCell, TableRow } from "@material-ui/core";
import { Typography, Button, DialogActions, Grid } from "@material-ui/core";

// Components
import MaturityLevel from "./MaturityLevel";
import DashboardCategoryResultPopover from "./DashboardCategoryResultPopover";

// Styles
import AssessmentDashboardStyle from "../../../../assets/styles/materialUI/questionnaire/assessmentResult/assessmentDashboard/AssessmentDashboardStyle";

// Providers
import { useLanguage } from "../../../../contexts/languageContext";

export default function AssessmentDashboard({
	selectedAssessment,
	dashboardOpen,
	submissionData,
	handleCloseDialogs,
	openTooltip,
	setOpenTooltip,
}) {
	let counter = 0;
	const { t } = useTranslation();
	const { direction } = useLanguage();
	const dashboardRef = useRef(null);
	const classes = AssessmentDashboardStyle();
	const [dataLoading, setDataLoading] = useState(false);

	const handleDownloadPDF = async () => {
		if (!dashboardRef.current) return;
		setDataLoading(true);
		try {
			const originalElements = dashboardRef.current.querySelectorAll("*");
			const computedStyles = Array.from(originalElements).map((el) => {
				const computed = window.getComputedStyle(el);
				return {
					width: computed.width,
					height: computed.height,
					backgroundColor: computed.backgroundColor,
					color: computed.color,
					float: computed.float,
				};
			});

			const clone = dashboardRef.current.cloneNode(true);
			clone.classList.add(classes.pdfClone);
			document.body.appendChild(clone);

			const clonedElements = clone.querySelectorAll("*");
			clonedElements.forEach((el, index) => {
				if (computedStyles[index]) {
					const styles = computedStyles[index];
					el.style.width = styles.width;
					el.style.height = styles.height;
					el.style.backgroundColor = styles.backgroundColor;
					el.style.color = styles.color;
					el.style.float = styles.float;
					el.style.direction = direction;
				}
			});

			await new Promise((r) => setTimeout(r, 200));
			const canvas = await html2canvas(clone, {
				scale: 2,
				useCORS: true,
				backgroundColor: "#FFFFFF",
			});
			document.body.removeChild(clone);
			const imgData = canvas.toDataURL("image/png");
			const pdf = new jsPDF("l", "mm", "a4");
			pdf.addImage(imgData, "PNG", 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
			let fileName = "";
			if (direction === "rtl") {
				fileName = `لوحة-مؤشرات-التقييم-${selectedAssessment?.date ?? "export"}.pdf`;
			} else {
				fileName = `assessment-dashboard-${selectedAssessment?.date ?? "export"}.pdf`;
			}

			pdf.save(fileName);
		} catch (err) {
			console.error("PDF error:", err);
		}
		setDataLoading(false);
	};

	const renderCategoryTooltipPopover = (subCategory, score, catIndex, subIndex, color, barLength, OddBarLength) => {
		return (
			<Grid className={classes.tooltipContainer}>
				<Grid className={classes.tooltipLeft}>
					{score < 5 && (
						<DashboardCategoryResultPopover
							score={score}
							subCategory={subCategory}
							setOpenTooltip={setOpenTooltip}
							barLength={OddBarLength}
							color={color}
							openTooltip={openTooltip}
							catIndex={catIndex}
							subIndex={subIndex}
							float={direction === "rtl" ? "left" : "right"}
						/>
					)}
				</Grid>
				<Grid className={classes.tooltipCenter}>
					{score == 5 && (
						<DashboardCategoryResultPopover
							score={score}
							subCategory={subCategory}
							setOpenTooltip={setOpenTooltip}
							barLength={"20px"}
							color={"#f9d578"}
							openTooltip={openTooltip}
							catIndex={catIndex}
							subIndex={subIndex}
						/>
					)}
				</Grid>
				<Grid className={classes.tooltipRight}>
					{score > 5 && (
						<DashboardCategoryResultPopover
							score={score}
							subCategory={subCategory}
							setOpenTooltip={setOpenTooltip}
							barLength={barLength}
							color={color}
							openTooltip={openTooltip}
							catIndex={catIndex}
							subIndex={subIndex}
						/>
					)}
				</Grid>
			</Grid>
		);
	};

	return (
		<Dialog
			open={dashboardOpen}
			onClose={handleCloseDialogs}
			maxWidth="lg"
			fullWidth
			className={classes.dialogContainer}
			dir={direction}
		>
			<DialogTitle className={classes.dialogTitle}>
				{t("assessment.results.dashboard.title", { date: selectedAssessment?.date })}
			</DialogTitle>
			<DialogContent ref={dashboardRef} id="dashboard-pdf" dividers={false} className={classes.dialogContent}>
				<Grid className={classes.contentGrid}>
					<Grid className={classes.headerContainer}>
						<Grid className={classes.headerFlex}>
							<Grid className={classes.headerLeft}>{t("assessment.results.dashboard.header")}</Grid>
							<Grid className={classes.headerCenter}>
								<Grid>
									{t("assessment.results.dashboard.maturityScore")}:{" "}
									<span color="#fff" className={classes.scoreText}>
										{" "}
										{submissionData?.overall_score?.toFixed(1)}/{submissionData?.overall_total}
									</span>
								</Grid>
							</Grid>
							<Grid className={classes.headerRight}>
								<Grid className={classes.headerRightFlex}>
									{t("assessment.results.dashboard.maturityLevel")}:{" "}
									<MaturityLevel score={submissionData?.overall_score} />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<br />
				{submissionData ? (
					<Table className={classes.table}>
						<TableBody>
							{submissionData?.categories?.map((category, catIndex) => {
								const subCount = category.subcategories.length;
								return category.subcategories.map((subCategory, subIndex) => {
									const score = (subCategory.score / subCategory.total_score) * 10;
									const color = score < 5 ? "#e5924c" : "#85c3ac";
									const barLength = `${(score / 5) * 100 - 100}%`;
									const OddBarLength = `${100 - ((score * 2) / 10) * 100}%`;
									const backgroundColors = counter % 2 === 0 ? "#000" : "#444";
									counter++;
									const blockIndex = Math.floor(catIndex / 5);
									const isFirstInBlock = catIndex % 5 === 0;
									let blockRowSpan = 0;
									if (isFirstInBlock) {
										const endIndex = Math.min(catIndex + 5, submissionData?.categories?.length);
										for (let i = catIndex; i < endIndex; i++) {
											blockRowSpan += submissionData?.categories[i].subcategories.length;
										}
									}
									return (
										<TableRow key={`${catIndex}-${subIndex}`}>
											{isFirstInBlock && subIndex === 0 && (
												<TableCell
													rowSpan={blockRowSpan}
													className={`${classes.pillarsCell} ${classes.mainHoverCell}`}
												>
													{
														[
															<Typography className={classes.pillarText}>
																{direction === "ltr" ? "Governance" : "الحوكمة"} <br />
																{direction === "ltr" ? "&" : "و"} <br />{" "}
																{direction === "ltr"
																	? "Culture of Integrity"
																	: "ثقافة النزاهة"}
															</Typography>,
															<Typography className={classes.pillarText}>
																{direction === "ltr" ? "Conduct" : "السلوك"}
																<br /> {direction === "ltr" ? "&" : "و"} <br />{" "}
																{direction === "ltr"
																	? "Operational Practices"
																	: "الممارسات التشغيلية"}
															</Typography>,
															<Typography className={classes.pillarText}>
																{direction === "ltr"
																	? "Strategic Influence"
																	: "التأثير الاستراتيجي"}{" "}
																<br />
																{direction === "ltr" ? "&" : "و"} <br />{" "}
																{direction === "ltr"
																	? "External Engagement"
																	: "العلاقات الخارجية"}
															</Typography>,
														][blockIndex % 3]
													}{" "}
												</TableCell>
											)}
											{subIndex === 0 && (
												<TableCell
													rowSpan={subCount}
													className={`${classes.categoryCell} ${classes.subHoverCell}`}
												>
													{direction === "ltr" ? category.category : category.category_ar}
												</TableCell>
											)}
											<TableCell
												style={{ backgroundColor: backgroundColors }}
												className={`${classes.subcategoryCell} ${classes.subHoverCell}`}
											>
												{direction === "ltr" ? subCategory.title : subCategory.title_ar}
											</TableCell>
											<TableCell
												style={{ backgroundColor: backgroundColors }}
												className={classes.scoreBarCell}
											>
												{renderCategoryTooltipPopover(
													subCategory,
													score,
													catIndex,
													subIndex,
													color,
													barLength,
													OddBarLength,
												)}
											</TableCell>
										</TableRow>
									);
								});
							})}
							<TableRow className={classes.scaleRow}>
								<TableCell colSpan={3} className={classes.scaleCellSpan} />
								<TableCell className={classes.scaleCell}>
									<Grid className={classes.scaleContainer}>
										<Grid className={classes.scaleInner}>
											{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
												<Grid key={n}>{n}</Grid>
											))}
										</Grid>
									</Grid>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				) : (
					<Typography variant="body2" color="textSecondary">
						{t("assessment.results.dashboard.noData")}
					</Typography>
				)}
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					className={`${classes.button} ${classes.downloadButton}`}
					onClick={handleDownloadPDF}
					variant="contained"
					color="primary"
				>
					{dataLoading ? <CircularProgress size={18} color="inherit" /> : t("common.buttons.download")}
				</Button>
				<Button
					className={`${classes.button} ${classes.closeButton}`}
					onClick={handleCloseDialogs}
					variant="contained"
					color="primary"
				>
					{t("common.buttons.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
